// Avvikelser (admin mockup §1, owner 2026-10-28): a list of the Inspected events with a
// "Hanterad" mark that records who and when. No task system until the shadow phase says so.

import { useEffect, useState } from "react";
import { Admin, type Deviation } from "../api/client.js";
import { t } from "../i18n.js";

const fmt = (iso: string) => new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });

export function describeDeviation(p: Deviation["payload"]): string {
  const d = p.detail ?? p.disposition ?? "";
  if (d === "erp-pick-reverted") return p.reason ?? t("dev.erpPickReverted");
  if (d === "erp-pick-line-reverted") return p.reason ?? t("dev.erpPickLineReverted");
  if (d === "erp-cancel-held") return p.reason ?? t("dev.erpCancelHeld");
  if (d === "automation-failed") return `${t("dev.automationFailed")}: ${p.reason ?? ""}`;
  if (d === "manual-entry") return [t("dev.manualEntry", { what: String((p as { observed?: string | number | null }).observed ?? "") }), p.gtin ? `GTIN ${p.gtin}` : null, p.lot ? `${t("adm.st.lot").toLowerCase()} ${p.lot}` : null, p.reason ? `"${p.reason}"` : null].filter(Boolean).join(" · ");
  const what = d === "unknown-product" ? t("dev.unknownProduct") : d === "missing-lot" ? t("dev.missingLot") : d === "missing-best-before" ? t("dev.missingBestBefore") : d === "quantity-unknown" ? t("dev.quantityUnknown") : d === "tally" ? t("banner.tally") : d === "unknown-container" ? t("dev.unknownContainer") : d;
  const ids = [p.gtin ? `GTIN ${p.gtin}` : null, p.lot ? `${t("adm.st.lot").toLowerCase()} ${p.lot}` : null, p.reason ? p.reason : null].filter(Boolean).join(" · ");
  return ids ? `${what} · ${ids}` : what;
}

export function Deviations() {
  const [filter, setFilter] = useState<"false" | "true" | "all">("false");
  const [rows, setRows] = useState<Deviation[] | null>(null);
  const [note, setNote] = useState<Record<string, string>>({});
  const load = () => Admin.deviations(filter).then(setRows).catch(() => setRows([]));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handle = async (d: Deviation) => {
    await Admin.handleDeviation(d.sequence, note[d.sequence]?.trim() || null);
    void load();
  };
  // A held cancellation (owner 2026-11-02): apply it, keep the orders open, or re-read the ERP after changing the floor.
  const [busy, setBusy] = useState<string | null>(null);
  const decide = async (d: Deviation, decision: "apply" | "dismiss") => {
    if (!d.hold) return;
    if (decision === "apply" && !confirm(t("adm.dev.hold.applyConfirm", { n: d.hold.count }))) return;
    setBusy(d.hold.id);
    try { await Admin.decideCancelHold(d.hold.id, decision); void load(); } finally { setBusy(null); }
  };
  const retry = async (d: Deviation) => {
    setBusy(d.sequence);
    try { await Admin.retryAutomation(d.sequence); void load(); } catch { alert(t("adm.dev.auto.notRetryable")); void load(); } finally { setBusy(null); }
  };
  const reread = async (d: Deviation) => {
    setBusy(d.hold?.id ?? "x");
    try { await Admin.syncRefresh("full"); alert(t("adm.dev.hold.rereadRequested")); } finally { setBusy(null); }
  };

  return (
    <>
      <div className="adm-top">
        <div><h1>{t("adm.deviations")}</h1><div className="crumb">{rows ? `${rows.length}` : ""}</div></div>
        <div className="right">
          <span className="seg">
            {(["false", "true", "all"] as const).map((f) => <button key={f} type="button" className={filter === f ? "on" : ""} onClick={() => setFilter(f)}>{f === "false" ? t("adm.dev.open") : f === "true" ? t("adm.dev.handled") : t("adm.dev.all")}</button>)}
          </span>
        </div>
      </div>
      <div className="adm-content">
        <div className="card">
          <div className="bd flush">
            <table className="at">
              <thead><tr><th>{t("adm.col.when")}</th><th>{t("adm.col.what")}</th><th>{t("adm.col.where")}</th><th>{t("adm.col.disposition")}</th><th>{t("adm.col.who")}</th><th>{t("adm.col.handled")}</th></tr></thead>
              <tbody>
                {rows?.length === 0 && <tr><td colSpan={6} className="mut">{t("adm.dev.none")}</td></tr>}
                {rows?.map((d) => (
                  <tr key={d.sequence}>
                    <td className="id mut">{fmt(d.at)}</td>
                    <td>{describeDeviation(d.payload)}</td>
                    <td className="mut">{d.reference ? `${t("adm.del.order")} ${d.reference}` : "—"}</td>
                    <td><span className="pill warn">{d.payload.disposition ?? ""}</span></td>
                    <td className="mut">{d.actorId.replace(/^user:/, "")}</td>
                    <td>
                      {d.handled ? (
                        <span className="pill ok" title={d.handled.note ?? ""}>{t("adm.dev.handledBy", { by: d.handled.by, at: fmt(d.handled.at) })}</span>
                      ) : d.hold?.state === "held" ? (
                        <span style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                          <button type="button" className="abtn sm" title={t("adm.dev.hold.changeFloor.hint")} onClick={() => { window.location.hash = "/settings"; }}>{t("adm.dev.hold.changeFloor")}</button>
                          <button type="button" className="abtn sm" disabled={busy === d.hold.id} onClick={() => void reread(d)}>{t("adm.dev.hold.reread")}</button>
                          <button type="button" className="abtn sm" disabled={busy === d.hold.id} onClick={() => void decide(d, "dismiss")}>{t("adm.dev.hold.dismiss")}</button>
                          <button type="button" className="abtn sm danger" disabled={busy === d.hold.id} onClick={() => void decide(d, "apply")}>{t("adm.dev.hold.apply", { n: d.hold.count })}</button>
                        </span>
                      ) : d.automation?.retryable ? (
                        <span style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                          <button type="button" className="abtn sm primary" disabled={busy === d.sequence} onClick={() => void retry(d)}>{t("adm.dev.auto.retry")}</button>
                          <button type="button" className="abtn sm" disabled={busy === d.sequence} onClick={async () => { await Admin.handleDeviation(d.sequence, t("adm.dev.auto.dismissed")); void load(); }}>{t("adm.dev.auto.dismiss")}</button>
                        </span>
                      ) : (
                        <span style={{ display: "flex", gap: 6 }}>
                          <input className="ain" style={{ height: 30, width: 180, borderRadius: 8, border: "1px solid var(--line)", background: "var(--bg)", padding: "0 8px", font: "inherit", color: "var(--text)" }} value={note[d.sequence] ?? ""} onChange={(e) => setNote({ ...note, [d.sequence]: e.target.value })} placeholder={t("adm.dev.handleNote")} />
                          <button type="button" className="abtn sm" onClick={() => void handle(d)}>{t("adm.dev.handle")}</button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
